<template>
    <div v-if="isSocialNetworksBlockVisible" class="OrgSocialNetworksBlock">
        <template v-if="buttonStyle">
            <div class="grid grid-cols-1 gap-3">
                <CButton
                    v-for="(value, key) in socialNetworks"
                    :key="key"
                    :href="value"
                    target="_blank"
                    rel="noopener"
                    :class="key === 'linkedin' ? 'organization-linkedin-button' : ''"
                    variant="secondary"
                >
                    <div class="flex items-center">
                        <CIcon :name="socialIconName(key)" class="mr-2 size-5" />
                        {{ socialNetworkName(key) }}
                    </div>
                </CButton>
            </div>
        </template>
        <template v-else>
            <div class="flex flex-wrap gap-3">
                <a
                    v-for="(value, key) in socialNetworks"
                    :key="key"
                    :href="value"
                    :aria-label="socialNetworkName(key)"
                    :class="key === 'linkedin' ? 'organization-linkedin-button' : ''"
                    target="_blank"
                    rel="noopener"
                    class="rounded-full bg-gray-100 dark:bg-subtle p-3 hover:bg-gray-200 hover:dark:bg-subtle hover:text-primary-600 hover:dark:text-primary-300"
                >
                    <CIcon :name="socialIconName(key)" class="size-5" />
                </a>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Org } from '#shared/types/Org'
import { getOrgSocialLinks } from '#shared/utils/orgProfile'
import { socialNetworks as socialNetworksConfig } from '@/constants/socialNetworks'
import CButton from '@/components/ui/CButton.vue'
import CIcon from '@/components/ui/CIcon.vue'

interface Props {
    org: Org
    buttonStyle?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    buttonStyle: false
})

// Links live in org.social (older documents kept them top-level); handles and
// "facebook.com/…" values are turned into full URLs, anything else is skipped.
const socialNetworks = computed(() => {
    return Object.fromEntries(getOrgSocialLinks(props.org).map(({ network, url }) => [network, url])) as Record<string, string>
})

const isSocialNetworksBlockVisible = computed(() => {
    return Object.keys(socialNetworks.value).length > 0
})

function socialNetworkName(key: string): string {
    return socialNetworksConfig.find((network) => network.key === key)?.name || key
}

function socialIconName(key: string): string {
    if (key === 'facebook') {
        return 'facebook-outline'
    }
    if (key === 'linkedin') {
        return 'linkedin-outline'
    }
    return key
}
</script>